import { useAuth0 } from '@auth0/auth0-react';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Modal from '@risksmart-app/components/src/modal';
import { useCallback, useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';

const PROMPT_DURATION_MS = 60 * 1000; // 1 minute
const DEFAULT_TIMEOUT_MS = 14400 * 1000; // 4 hours
const COUNTDOWN_TICK_MS = 500;

// Recomputed from the deadline on every tick rather than decremented, so the
// countdown cannot drift. The tradeoff of using wall-clock time is that a system
// clock correction shifts the displayed number; accepted, because the alternative
// (`getRemainingTime()`) misreports the grace period outright — see handleOnPrompt.
const secondsUntil = (deadlineMs: number) =>
  Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));

const IdleSessionTimeout = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const [timeoutDuration, setTimeoutDuration] = useState(DEFAULT_TIMEOUT_MS);
  /**
   * Wall-clock instant the user will be logged out, or null while no warning is
   * showing. This is the single source of truth for both the dialog's visibility and
   * its countdown, so the two can no longer disagree (COI-605).
   */
  const [logoutDeadline, setLogoutDeadline] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const idleTimeoutSeconds = user?.claims_organization_idle_timeout;
    if (idleTimeoutSeconds && typeof idleTimeoutSeconds === 'string') {
      const timeoutMs = parseInt(idleTimeoutSeconds, 10) * 1000;
      // Ensure timeout is greater than the prompt time
      if (timeoutMs > PROMPT_DURATION_MS && timeoutMs !== timeoutDuration) {
        setTimeoutDuration(timeoutMs);
        // Changing `timeout` makes react-idle-timer restart its whole cycle, and it
        // only emits `onActive` on that path if it was already *idle* — not if it was
        // merely prompted. So a duration change while the warning is showing would
        // drop the library out of its prompted state without telling us, leaving the
        // dialog open, its countdown clamped at 0, and no `onIdle` arriving until a
        // fresh full timeout elapsed. Close it here so it can never outlive the
        // prompt it belongs to.
        setLogoutDeadline(null);
      }
    }
  }, [user, timeoutDuration]);

  const handleLogout = useCallback(() => {
    setLogoutDeadline(null);
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const handleOnActive = useCallback(() => setLogoutDeadline(null), []);

  /**
   * Anchor the countdown on the prompt itself rather than on the timer's
   * `getRemainingTime()`. react-idle-timer reschedules `onIdle` for
   * `promptBeforeIdle` ms from the moment it emits `onPrompt`, so the prompt event is
   * what actually determines when logout happens — whereas `getRemainingTime()`
   * reports `timeout - elapsed`, which under-reports the grace period whenever the
   * prompt fires late (a throttled background tab, or a machine resuming from sleep).
   *
   * Seeding `remaining` here, in the same commit that opens the dialog, is what keeps
   * the first paint honest: it used to render whatever `remaining` already held — `0`
   * on the first prompt of a session — until the countdown interval's first tick
   * landed 500ms later, which is the "Time remaining: 0 seconds" users reported.
   */
  const handleOnPrompt = useCallback(() => {
    const deadline = Date.now() + PROMPT_DURATION_MS;
    setLogoutDeadline(deadline);
    setRemaining(secondsUntil(deadline));
  }, []);

  const { activate } = useIdleTimer({
    onIdle: handleLogout,
    onActive: handleOnActive,
    onPrompt: handleOnPrompt,
    timeout: timeoutDuration,
    promptBeforeIdle: PROMPT_DURATION_MS,
    throttle: 500,
    crossTab: true,
    syncTimers: 500,
  });

  useEffect(() => {
    if (logoutDeadline === null) {
      return;
    }

    const interval = setInterval(() => {
      setRemaining(secondsUntil(logoutDeadline));
    }, COUNTDOWN_TICK_MS);

    return () => clearInterval(interval);
  }, [logoutDeadline]);

  const handleStayLoggedIn = async () => {
    try {
      await getAccessTokenSilently();
      activate();
      setLogoutDeadline(null);
    } catch (error) {
      console.error('Failed to refresh session, logging out:', error);
      handleLogout();
    }
  };

  return (
    <Modal
      visible={logoutDeadline !== null}
      header={'Session Timeout Warning'}
      onDismiss={handleStayLoggedIn}
    >
      <p>
        {
          'You have been inactive. For your security, you will be logged out automatically.'
        }
      </p>
      <p>
        {'Time remaining: '}
        <strong>
          {remaining}
          {' seconds'}
        </strong>
      </p>
      <div
        className={
          'mt-6 items-end flex justify-end border-y-1 border-slate-400'
        }
      >
        <SpaceBetween size={'s'} direction={'horizontal'}>
          <Button variant={'primary'} onClick={handleStayLoggedIn}>
            {'Stay Logged In'}
          </Button>
          <Button onClick={handleLogout}>{'Log Out'}</Button>
        </SpaceBetween>
      </div>
    </Modal>
  );
};

export default IdleSessionTimeout;
